import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import { getOrganizationId } from "Libs/utils";
import { currentTestTrialSelector } from "Reducers/testMode";
import { AsyncThunkOptionType } from "Reducers/types";

import {
  getTrialRequest,
  OrganizationTrial
} from "Api/organizations/trial/getTrial.request";

import type { RootState } from "Store/configureStore";

export const getOrganizationTrial = createAsyncThunk<
  OrganizationTrial | undefined,
  { organizationId: string },
  AsyncThunkOptionType
>(
  "app/organization/trial",
  async ({ organizationId }, { getState, rejectWithValue }) => {
    try {
      const orgId = getOrganizationId(getState, organizationId);

      if (!orgId) {
        return undefined;
      }

      const response = await getTrialRequest(orgId);
      return response.trial;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

type OrganizationTrialState = {
  data?: Record<
    string,
    {
      loading?: boolean;
      data?: OrganizationTrial;
      errors?: string;
    }
  >;
};

const initialState: OrganizationTrialState = {};

const organizationTrial = createSlice({
  name: "organizationTrial",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOrganizationTrial.pending, (state, { meta }) => {
        setDeep(state, ["data", meta.arg.organizationId, "loading"], true);
      })
      .addCase(getOrganizationTrial.fulfilled, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["data", organizationId, "loading"], false);
        setDeep(state, ["data", organizationId, "data"], action.payload);
      })
      .addCase(getOrganizationTrial.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["data", organizationId, "loading"], false);
        setDeep(
          state,
          ["data", organizationId, "errors"],
          action.error.message
        );
      });
  }
});

export default organizationTrial.reducer;

// Base selector
export const organizationTrialSelector = (
  state: RootState,
  props: { organizationId: string }
) => {
  return state.organizationTrial?.data?.[props.organizationId]?.data;
};

// Loading selector
export const selectIsLoadingOrganizationTrial = (
  state: RootState,
  organizationId: string
) => state.organizationTrial?.data?.[organizationId]?.loading;

// Error selector
export const organizationTrialErrorSelector = (
  state: RootState,
  organizationId: string
) => state.organizationTrial?.data?.[organizationId]?.errors;

// Trial with test mode fallback (matches organizationProfileCurrenTrialSelector behavior)
// The test-mode trial wins so the testing widget can drive trial state without
// an API trial. Its date picker yields a Date, so normalize to the ISO string
// every consumer of `expiration` already expects.
export const currentTrialSelector = createSelector(
  currentTestTrialSelector,
  organizationTrialSelector,
  (testTrial, trial) =>
    testTrial
      ? {
          ...testTrial,
          expiration:
            testTrial.expiration instanceof Date
              ? testTrial.expiration.toISOString()
              : testTrial.expiration
        }
      : trial
);

// Active status selector - maps status to boolean
export const selectTrialActive = createSelector(
  currentTrialSelector,
  trial => trial?.status === "active"
);

// Expiration selector
export const selectTrialExpiration = createSelector(
  currentTrialSelector,
  trial => trial?.expiration
);

// Days remaining selector
export const selectTrialDaysRemaining = createSelector(
  currentTrialSelector,
  trial => trial?.days_remaining
);

// Resource limit selector (only exists on OrganizationTrial, not test trial)
export const selectTrialResourceLimit = createSelector(
  currentTrialSelector,
  trial => {
    // Type guard: check if this is OrganizationTrial (has resource_limit)
    if (trial && "resource_limit" in trial) {
      return trial.resource_limit;
    }
    return undefined;
  }
);

// Created at selector (only exists on OrganizationTrial, not test trial)
export const selectTrialCreatedAt = createSelector(
  currentTrialSelector,
  trial => {
    if (trial && "created_at" in trial) {
      return trial.created_at;
    }
    return undefined;
  }
);

// Updated at selector (only exists on OrganizationTrial, not test trial)
export const selectTrialUpdatedAt = createSelector(
  currentTrialSelector,
  trial => {
    if (trial && "updated_at" in trial) {
      return trial.updated_at;
    }
    return undefined;
  }
);

// Organization ID selector (only exists on OrganizationTrial, not test trial)
export const selectTrialOrganizationId = createSelector(
  currentTrialSelector,
  trial => {
    if (trial && "organization_id" in trial) {
      return trial.organization_id;
    }
    return undefined;
  }
);

// Billing account ID selector (only exists on OrganizationTrial, not test trial)
export const selectTrialBillingAccountId = createSelector(
  currentTrialSelector,
  trial => {
    if (trial && "billing_account_id" in trial) {
      return trial.billing_account_id;
    }
    return undefined;
  }
);
